/**
 * SEFAZ queries and mutations for TanStack Query (Vendor App)
 *
 * This module provides queries for SEFAZ status and mutations for contingency management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { queryKeys } from '../keys'
import { queryGet, queryPost } from '../utils/request'

// Type definitions
export interface SefazStatus {
  authorizator: string
  environment: string
  status: string
  last_update: string
  in_contingency: boolean
  contingency_reason?: string
}

export interface ContingencyParams {
  authorizator: string
  environment: string
}

// Cache configuration
const CACHE_TIMES = {
  STATUS: 30 * 1000, // 30 seconds - status changes frequently
}

/**
 * QUERIES
 */

interface SefazStatusResponse {
  items: SefazStatus[]
}

/**
 * Query hook to fetch SEFAZ statuses
 */
export const useSefazStatusQuery = () => {
  return useQuery({
    queryKey: queryKeys.sefazStatus(),
    queryFn: async (): Promise<SefazStatus[]> => {
      const response = await queryGet<SefazStatusResponse>('/v1/sefaz/status')
      return response.items
    },
    staleTime: CACHE_TIMES.STATUS,
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}

/**
 * MUTATIONS
 */

/**
 * Mutation hook to force contingency mode
 */
export const useForceContingencyMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: ContingencyParams): Promise<any> => {
      return queryPost<any>('/v1/sefaz/force-contingency', params)
    },
    onSuccess: () => {
      // Invalidate SEFAZ status to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.sefazStatus() })
    },
  })
}

/**
 * Mutation hook to clear contingency mode
 */
export const useClearContingencyMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: ContingencyParams): Promise<any> => {
      return queryPost<any>('/v1/sefaz/clear-contingency', params)
    },
    onSuccess: () => {
      // Invalidate SEFAZ status to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.sefazStatus() })
    },
  })
}

/**
 * Hook that combines SEFAZ queries and mutations for convenient usage
 */
export const useSefazOperations = () => {
  return {
    // Queries
    useSefazStatusQuery,

    // Mutations
    useForceContingencyMutation,
    useClearContingencyMutation,
  }
}
